import { useContext, useEffect, useMemo, useState } from "react";

import { ClientContext } from "../ClientContext";

export type FlightTripDetailsInterceptionStatus =
  | "none"
  | "pending"
  | "handled"
  | "declined";

export interface UseFlightTripDetailsInterceptionArgs {
  /** The selected flight's `bookedItinerary.id`, when a flight is open. */
  itineraryId?: string;
  /**
   * True while an in-list exercise flow (CFAR / ChFAR / disruption) owns the
   * screen — interception is suspended so the host cannot hijack an ongoing
   * exercise.
   */
  suspended?: boolean;
}

/**
 * Consults the host's `onFlightTripDetailsOpen` hook (see
 * `TripsClientAssetProps.onFlightTripDetailsOpen`) when a flight's trip
 * details are about to open.
 *
 * The host hook is invoked from an effect — never during render — and at
 * most once per selected itinerary, since the host may navigate away
 * (e.g. Nubank redirecting flight trip details to the commerce portal,
 * NU-403). Status:
 *
 * - `"none"`     — no host hook configured / no flight selected / suspended;
 * - `"pending"`  — the hook exists but has not been consulted yet;
 * - `"handled"`  — the host took over navigation;
 * - `"declined"` — the host declined; render the details as usual.
 *
 * `showLoader` is true while `pending` or `handled`, so callers can render
 * a loading state instead of the details and the legacy screen never
 * flashes during a host redirect. Tenants that don't provide the host hook
 * always get `"none"` and are untouched.
 */
export function useFlightTripDetailsInterception({
  itineraryId,
  suspended = false,
}: UseFlightTripDetailsInterceptionArgs): {
  status: FlightTripDetailsInterceptionStatus;
  showLoader: boolean;
} {
  const clientContext = useContext(ClientContext);
  const onFlightTripDetailsOpen = clientContext?.onFlightTripDetailsOpen;

  const [intercept, setIntercept] = useState<{
    itineraryId: string;
    handled: boolean;
  } | null>(null);

  useEffect(() => {
    if (
      !onFlightTripDetailsOpen ||
      !itineraryId ||
      suspended ||
      intercept?.itineraryId === itineraryId
    ) {
      return;
    }
    setIntercept({
      itineraryId,
      handled: onFlightTripDetailsOpen(itineraryId),
    });
  }, [onFlightTripDetailsOpen, itineraryId, suspended, intercept]);

  const status = useMemo<FlightTripDetailsInterceptionStatus>(() => {
    if (!onFlightTripDetailsOpen || !itineraryId || suspended) {
      return "none";
    }
    if (intercept?.itineraryId !== itineraryId) {
      return "pending";
    }
    return intercept.handled ? "handled" : "declined";
  }, [onFlightTripDetailsOpen, itineraryId, suspended, intercept]);

  return {
    status,
    showLoader: status === "pending" || status === "handled",
  };
}
